import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Typography,
  Divider,
  Button,
  Chip,
  Avatar,
  Paper,
  Alert,
  Fade,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AccountBalance as AccountBalanceIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatINR } from '../utils/currency';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuth } from '../contexts/AuthContext';

dayjs.extend(relativeTime);

const NotificationCenter = () => {
  const { user, isAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set());

  useEffect(() => {
    // Load dismissed notifications from localStorage
    const dismissed = localStorage.getItem('dismissedNotifications');
    if (dismissed) {
      setDismissedNotifications(new Set(JSON.parse(dismissed)));
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch various notification sources
      const [paymentsRes, penaltiesRes, meetingsRes] = await Promise.all([
        axios.get('/api/payments'),
        axios.get('/api/penalties'),
        axios.get('/api/meetings'),
      ]);

      const newNotifications = [];
      const currentMonth = dayjs().format('YYYY-MM');
      const now = dayjs();

      // Late payment notifications
      const userPayments = isAdmin 
        ? paymentsRes.data 
        : paymentsRes.data.filter(p => p.user_id === user.id);
      
      const currentMonthPayment = userPayments.find(p => p.payment_month === currentMonth);

      // The system admin does not contribute, so has nothing to be late on
      if (!user?.isSystemAdmin && (!currentMonthPayment || currentMonthPayment.status === 'pending')) {
        const daysLate = now.date() - 5; // Assuming 5th is the deadline
        if (daysLate > 0) {
          newNotifications.push({
            id: `late-payment-${currentMonth}`,
            type: 'payment',
            title: 'Late Payment Alert',
            message: `Your payment for ${currentMonth} is ${daysLate} days overdue`,
            severity: daysLate > 10 ? 'error' : 'warning',
            timestamp: now.subtract(daysLate, 'days'),
            action: 'Make Payment',
            actionPath: '/payments',
            isRead: false,
          });
        }
      }

      // Pending penalty notifications
      const userPenalties = isAdmin 
        ? penaltiesRes.data 
        : penaltiesRes.data.filter(p => p.user_id === user.id);
      
      const pendingPenalties = userPenalties.filter(p => p.status === 'pending');
      
      if (pendingPenalties.length > 0) {
        const totalAmount = pendingPenalties.reduce((sum, p) => sum + parseFloat(p.amount), 0);
        newNotifications.push({
          id: 'pending-penalties',
          type: 'penalty',
          title: 'Pending Penalties',
          message: `You have ${pendingPenalties.length} pending penalties (${formatINR(totalAmount)})`,
          severity: 'warning',
          timestamp: dayjs(pendingPenalties[0].penalty_date),
          action: 'View Penalties',
          actionPath: '/penalties',
          isRead: false,
        });
      }

      // Upcoming meeting notifications
      const upcomingMeetings = meetingsRes.data.filter(m => {
        const meetingDate = dayjs(m.meeting_date);
        return meetingDate.isAfter(now) && meetingDate.isBefore(now.add(7, 'days'));
      });

      upcomingMeetings.forEach(meeting => {
        const meetingDate = dayjs(meeting.meeting_date);
        const daysUntil = meetingDate.diff(now, 'days');
        
        newNotifications.push({
          id: `meeting-${meeting.id}`,
          type: 'meeting',
          title: 'Upcoming Meeting',
          message: `Meeting on ${meetingDate.format('MMM D')} (${daysUntil === 0 ? 'Today' : `in ${daysUntil} days`})`,
          severity: daysUntil <= 1 ? 'info' : 'default',
          timestamp: meetingDate,
          action: 'View Meetings',
          actionPath: '/meetings',
          isRead: false,
        });
      });

      // Admin-only notifications
      if (isAdmin) {
        // Overdue payments summary
        const overduePayments = paymentsRes.data.filter(p => {
          const paymentMonth = dayjs(p.payment_month);
          return p.status === 'pending' && paymentMonth.isBefore(currentMonth);
        });

        if (overduePayments.length > 0) {
          newNotifications.push({
            id: 'admin-overdue-payments',
            type: 'admin',
            title: 'System Alert: Overdue Payments',
            message: `${overduePayments.length} members have overdue payments`,
            severity: 'error',
            timestamp: now,
            action: 'Admin Panel',
            actionPath: '/admin',
            isRead: false,
          });
        }

        // Monthly collection summary
        const currentMonthPayments = paymentsRes.data.filter(p => p.payment_month === currentMonth);
        const completedPayments = currentMonthPayments.filter(p => p.status === 'completed');
        
        if (currentMonthPayments.length > 0) {
          const completionRate = (completedPayments.length / currentMonthPayments.length) * 100;
          
          if (completionRate < 80 && now.date() > 10) {
            newNotifications.push({
              id: 'admin-collection-rate',
              type: 'admin',
              title: 'Low Collection Rate',
              message: `Only ${completionRate.toFixed(0)}% of payments collected this month`,
              severity: 'warning',
              timestamp: now,
              action: 'Send Reminders',
              actionPath: '/admin',
              isRead: false,
            });
          }
        }
      }

      // Sort by timestamp (newest first)
      newNotifications.sort((a, b) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix());

      // Filter out dismissed notifications
      const visibleNotifications = newNotifications.filter(n => !dismissedNotifications.has(n.id));

      // Debug logging
      console.log('Generated notifications:', newNotifications.map(n => n.id));
      console.log('Dismissed notifications:', [...dismissedNotifications]);
      console.log('Visible notifications:', visibleNotifications.map(n => n.id));

      setNotifications(visibleNotifications);
      setUnreadCount(visibleNotifications.filter(n => !n.isRead).length);
      
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.id, dismissedNotifications]);

  // Fetch notifications when component mounts and dependencies change
  useEffect(() => {
    fetchNotifications();
    
    // Set up periodic notification checks (every 5 minutes)
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Save dismissed notifications to localStorage whenever it changes
  useEffect(() => {
    if (dismissedNotifications.size > 0) {
      localStorage.setItem('dismissedNotifications', JSON.stringify([...dismissedNotifications]));
    }
  }, [dismissedNotifications]);

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationAction = (notification) => {
    // Mark as read
    markAsRead(notification.id);
    
    // Navigate to action path
    window.location.href = notification.actionPath;
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, isRead: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const dismissNotification = (notificationId, event) => {
    event.stopPropagation(); // Prevent triggering the notification action
    const newDismissedSet = new Set([...dismissedNotifications, notificationId]);
    setDismissedNotifications(newDismissedSet);
    
    // Immediately save to localStorage
    localStorage.setItem('dismissedNotifications', JSON.stringify([...newDismissedSet]));
    
    // Remove from current notifications
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    
    // Update unread count
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    toast.success('Notification dismissed');
  };

  const clearAllNotifications = () => {
    const allIds = notifications.map(n => n.id);
    const newDismissedSet = new Set([...dismissedNotifications, ...allIds]);
    setDismissedNotifications(newDismissedSet);
    
    // Immediately save to localStorage
    localStorage.setItem('dismissedNotifications', JSON.stringify([...newDismissedSet]));
    
    setNotifications([]);
    setUnreadCount(0);
    toast.success('All notifications cleared');
  };

  const restoreDismissedNotifications = () => {
    setDismissedNotifications(new Set());
    localStorage.removeItem('dismissedNotifications');
    fetchNotifications();
    toast.success('Dismissed notifications restored');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'payment': return <PaymentIcon />;
      case 'penalty': return <WarningIcon />;
      case 'meeting': return <EventIcon />;
      case 'admin': return <AccountBalanceIcon />;
      default: return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (severity) => {
    switch (severity) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'success': return 'success';
      default: return 'default';
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="large"
        color="inherit"
        onClick={handleNotificationClick}
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 400 },
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: { xs: '75vh', sm: 500 },
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1} mb={1}>
            <Typography variant="h6">
              Notifications
              {notifications.length > 0 && (
                <Chip 
                  label={notifications.length} 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Box display="flex" gap={1}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  onClick={markAllAsRead}
                  startIcon={<CheckCircleIcon />}
                  variant="outlined"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  size="small"
                  onClick={clearAllNotifications}
                  startIcon={<DeleteIcon />}
                  color="error"
                  variant="outlined"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          </Box>

          {dismissedNotifications.size > 0 && (
            <Box mb={2}>
              <Alert 
                severity="info" 
                action={
                  <Button 
                    size="small" 
                    onClick={restoreDismissedNotifications}
                    color="inherit"
                  >
                    Restore
                  </Button>
                }
              >
                {dismissedNotifications.size} notification(s) dismissed
              </Alert>
            </Box>
          )}

          {notifications.length === 0 ? (
            <Box textAlign="center" py={3}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: { xs: 'none', sm: 400 }, overflow: { xs: 'visible', sm: 'auto' } }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    disablePadding
                    sx={{
                      opacity: notification.isRead ? 0.6 : 1,
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleNotificationAction(notification)}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemIcon>
                        <Avatar 
                          sx={{ 
                            bgcolor: `${getNotificationColor(notification.severity)}.main`,
                            width: 36,
                            height: 36
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                              {notification.title}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <IconButton
                                size="small"
                                onClick={(e) => dismissNotification(notification.id, e)}
                                sx={{ 
                                  opacity: 0.6,
                                  '&:hover': { opacity: 1 },
                                  padding: '4px'
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                              {!notification.isRead && (
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    bgcolor: 'primary.main', 
                                    borderRadius: '50%'
                                  }} 
                                />
                              )}
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {dayjs(notification.timestamp).fromNow()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter;